<script setup lang="ts">
import type { PeriodI, UniqueLabel } from "@lxc/app-device-common";
import { filterEmptyValues, IntlDateTools } from "@lxc/app-device-common";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import type { Ref } from "vue";
import { watch } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import { FILTER_DATE_FORMAT } from "~/core/constants/constants";
import LxcError from "~/core/models/LxcError.class";
import type { ReactiveArrayObject } from "~/core/models/ReactiveArrayObject.interface";
import type {
  FilterFormSection,
  FilterSelectionDefinition,
  FilterSelectionValue,
  FiltersSelection,
  Option,
} from "~/core/models/filters";
import { FilterInputType, Filters } from "~/core/models/filters";
import { getPeriod } from "~/core/utils/date";
import filtersUtils from "~/core/utils/filters.utils";
import { NotificationKey } from "~/core/utils/notifications";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { useActionsActionTypes } from "~/modules/log/composables/useActionsActionTypes";
import { ActionsOrActionTypesContext } from "~/modules/log/models/ActionsOrActionTypesContext.enum";
import type { AsyncLog } from "~/modules/log/models/AsyncLog.interface";
import type { LogComponentId } from "~/modules/log/models/LogComponentId.enum";
import { LogEntityClass } from "~/modules/log/models/LogEntityClass.enum";
import { LogEventType } from "~/modules/log/models/LogEventType.enum";
import { LogLevel } from "~/modules/log/models/LogLevel.enum";
import type { LogSortBy } from "~/modules/log/models/LogSortBy.enum";
import type { LogSortDirection } from "~/modules/log/models/LogSortDirection.enum";
import { Origin } from "~/modules/log/models/Origin.enum";
import type { TranslatedLogAttribute } from "~/modules/log/models/TranslatedLogAttribute.interface";
import SectorsService from "~/modules/sector/services/sectors.service";
import type { Role } from "~/modules/user/models/Role.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";
import type { UserProfile } from "~/modules/user/models/UserProfile.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";
import userGroupService from "~/modules/user/services/userGroup.service";
import userProfileService from "~/modules/user/services/userProfileSuperAdministration.service";

const props = defineProps<{
  filters: FiltersSelection;
  rowsSelected: AsyncLog[];
  businessUnitCode?: number;
  organizationCode?: number;
  logs?: AsyncLog[];
}>();
const emit = defineEmits(["change", "enter"]);

const { locale, t } = useI18n();
const { fetchAllActions, fetchAllActionTypes } = useActionsActionTypes({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  results: resultDtwins,
  setFilter: setFilterDtwin,
  fetchData: fetchDataDtwin,
  error: errorDtwin,
} = useFetchDtwins({
  organizationCode: props.organizationCode,
  searchMode: SearchMode.FILTER_SEARCH,
  useQueryParametersForPagination: false,
});

const isMounted = ref(false);
const updateInProgress = ref(false);
const applyPending = ref(false);
let onPropsEntityChangedPending = false;

const componentId: Ref<LogComponentId | undefined> = ref(
  props.filters.get(Filters.LOG_COMPONENT_ID)?.value as
    | LogComponentId
    | undefined,
);
const entityClass: Ref<LogEntityClass | undefined> = ref();
const entityId: Ref<string[] | undefined> = ref();
const actions: Ref<TranslatedLogAttribute[] | undefined> = ref();
const actionTypes: Ref<TranslatedLogAttribute[] | undefined> = ref();
const devices = reactive<ReactiveArrayObject<Dtwin>>({ value: [] });
const users = reactive<ReactiveArrayObject<User>>({ value: [] });
const appliedActions = ref<TranslatedLogAttribute[] | undefined>();
const appliedActionTypes = ref<TranslatedLogAttribute[] | undefined>();
const cacheActionAttr = ref<TranslatedLogAttribute[] | LxcError>();
const cacheActionTypesAttr = ref<TranslatedLogAttribute[] | LxcError>();
const appliedDevices = reactive<ReactiveArrayObject<Dtwin>>({
  value: [],
});
const appliedUserGroups = reactive<ReactiveArrayObject<UserGroup>>({
  value: [],
});
const appliedProfiles = reactive<ReactiveArrayObject<UserProfile>>({
  value: [],
});
const appliedRoles = reactive<ReactiveArrayObject<Role>>({ value: [] });
const appliedSectors = reactive<ReactiveArrayObject<UserSessionSector>>({
  value: [],
});
const appliedUsers = reactive<ReactiveArrayObject<User>>({ value: [] });
const sortBy: Ref<LogSortBy | undefined> = ref(
  props.filters.get(Filters.SORT_BY)?.value as LogSortBy | undefined,
);
const sortDirection: Ref<LogSortDirection | undefined> = ref(
  props.filters.get(Filters.SORT_DIRECTION)?.value as
    | LogSortDirection
    | undefined,
);
const isDevicesLoading = ref(false);
const isUsersLoading = ref(false);
const isUserGroupsLoading = ref(false);
const isProfileLoading = ref(false);
const isRolesLoading = ref(false);
const isSectorLoading = ref(false);
const tagsetId = `tagset-${uuidv4()}`;
const tagsetSelector = `#${tagsetId}`;

const deviceLabel = t("logs.filters.entityClass.value.device");
const sectorLabel = t("logs.filters.entityClass.value.sector");
const profileLabel = t("logs.filters.entityClass.value.profile");
const roleLabel = t("logs.filters.entityClass.value.role");
const userGroupLabel = t("logs.filters.entityClass.value.group");
const userLabel = t("logs.filters.entityClass.value.user");
const actionLabel = t("logs.action.label");
const actionTypeLabel = t("logs.actionType.label");
const periodSeparator = ref(` ${t("logs.filters.timestamp.periodSeparator")} `);
const eventTypeLabel = t("logs.eventType.label");
const levelLabel = t("logs.level.label");
const originLabel = t("logs.origin.label");

const isEntityLoading = computed((): boolean => {
  return (
    isDevicesLoading.value ||
    isUsersLoading.value ||
    isUserGroupsLoading.value ||
    isProfileLoading.value ||
    isSectorLoading.value ||
    isRolesLoading.value
  );
});

const selectedFiltersByTypes = computed<
  (
    | string
    | LogComponentId
    | LogEntityClass
    | (string | LogLevel | undefined | null)[]
    | undefined
    | null
  )[]
>(() => [
  componentId.value,
  entityId.value,
  actions.value?.map((translated) => translated.rawValue),
  actionTypes.value?.map((translated) => translated.rawValue),
]);

const formPeriod = computed({
  get(): PeriodI {
    const appliedStartStringDate = props.filters.get(Filters.LOG_START_DATE)
      ?.value as string | undefined;
    const appliedEndStringDate = props.filters.get(Filters.LOG_END_DATE)
      ?.value as string | undefined;

    if (appliedStartStringDate && appliedEndStringDate) {
      return {
        startDate: IntlDateTools.parse(
          appliedStartStringDate,
          FILTER_DATE_FORMAT,
          locale.value,
        ),
        endDate: IntlDateTools.parse(
          appliedEndStringDate,
          FILTER_DATE_FORMAT,
          locale.value,
        ),
      };
    } else {
      return getPeriod();
    }
  },
  set(newValue: PeriodI) {
    const { startDate, endDate } = getPeriod(
      newValue?.startDate,
      newValue?.endDate,
    );

    emit("change", Filters.LOG_START_DATE, dayjs(startDate).format());
    emit("change", Filters.LOG_END_DATE, dayjs(endDate).format());
    emit("enter");
  },
});

const canClearPeriodSelection = computed(() => {
  const todayPeriod = getPeriod();
  const selectedPeriod = getPeriod(
    formPeriod.value.startDate,
    formPeriod.value.endDate,
  );
  return (
    todayPeriod.startDate?.getTime() !== selectedPeriod.startDate?.getTime() ||
    todayPeriod.endDate?.getTime() !== selectedPeriod.endDate?.getTime()
  );
});

function errorFetchAppliedEntities<T>(
  response: LxcError,
  entityIdsToFetch: string[],
  primaryKey: string,
) {
  let errorResult: T[] = [];
  if (response.status === 404 || response.status === 410) {
    // If the resource does not exist or is deleted, then display the filter with the id
    errorResult = entityIdsToFetch.map((entityId) => {
      const fakeEntity: any = {};
      fakeEntity[primaryKey] = entityId;
      fakeEntity.label = entityId;
      return fakeEntity as T;
    });
  } else {
    response.notify(NotificationKey.error);
  }
  return errorResult;
}

/**
 * Fetches and processes entities based on a list of entity IDs.
 *
 * <T> The type of entities being processed.
 * @param appliedList The list of already applied entities.
 * @param service The service used to fetch entity details.
 * @param detailsMethod The method name used to fetch entity details from the service.
 * @param primaryKey The primary key used to identify entities.
 * @param loading A reference to a boolean indicating the loading state.
 * @param entityIds (Optional) The list of entity IDs to fetch.
 * @param list (Optional) An additional list of entities to check against.
 * @param specialLogic Callback for special logic
 * @return A promise that resolves to an array of entities.
 * @throws SomeException If an error occurs during fetching.
 */
async function fetchAppliedEntities<T>(
  appliedList: ReactiveArrayObject<T>,
  service: any,
  detailsMethod: string,
  primaryKey: string,
  loading: Ref<boolean>,
  entityIds?: string[],
  list?: ReactiveArrayObject<T>,
  specialLogic?: (entityIdsToFetch: string[]) => Promise<T[]>,
): Promise<T[]> {
  let entitiesResult: T[] = [];

  if (entityIds && !loading.value) {
    loading.value = true;
    let entityIdsToFetch: string[] = [];

    if (list && list.value.length > 0) {
      entityIdsToFetch = entityIds.filter(
        (paramId) =>
          list.value.every(
            (currentEntity: T) =>
              (currentEntity as any)[primaryKey] !== paramId,
          ) &&
          appliedList.value.every(
            (currentEntity) => (currentEntity as any)[primaryKey] !== paramId,
          ),
      );

      entitiesResult = list.value
        .filter((currentEntity: T) =>
          entityIds.includes((currentEntity as any)[primaryKey] ?? ""),
        )
        .concat(
          appliedList.value.filter((currentEntity) => {
            return (
              entityIds.includes((currentEntity as any)[primaryKey] ?? "") &&
              !list.value.some(
                (pEntity: T) =>
                  (pEntity as any)[primaryKey] ===
                  (currentEntity as any)[primaryKey],
              )
            );
          }),
        );
    } else {
      entityIdsToFetch = entityIds.filter((paramId) =>
        appliedList.value.every(
          (currentEntity) => (currentEntity as any)[primaryKey] !== paramId,
        ),
      );
    }

    if (entityIdsToFetch.length > 0) {
      if (specialLogic) {
        entitiesResult = entitiesResult.concat(
          await specialLogic(entityIdsToFetch),
        );
      } else {
        // Fetch the devices name if the device entityId is in the URL when the page is loaded.
        const response = await service[detailsMethod](entityIdsToFetch);

        if (LxcError.check(response)) {
          entitiesResult = entitiesResult.concat(
            errorFetchAppliedEntities(response, entityIdsToFetch, primaryKey),
          );
        } else {
          entitiesResult = entitiesResult.concat(response);
        }
      }
    }
    loading.value = false;
  }

  return entitiesResult;
}

async function fetchAppliedDevices(deviceIds?: string[]) {
  appliedDevices.value = await fetchAppliedEntities<Dtwin>(
    appliedDevices,
    undefined,
    "",
    "uid",
    isDevicesLoading,
    deviceIds,
    devices,
    async (entityIdsToFetch) => {
      setFilterDtwin(Filters.DTWIN_UID, entityIdsToFetch);
      await fetchDataDtwin(0, entityIdsToFetch.length);
      let response: Dtwin[] = [];
      if (
        errorDtwin.value ||
        !resultDtwins.value ||
        resultDtwins.value?.results?.length === 0
      ) {
        if (
          !errorDtwin.value &&
          (!resultDtwins.value || resultDtwins.value?.results?.length === 0)
        ) {
          errorDtwin.value = new LxcError({
            type: "DEVICE_TWIN_NOT_FOUND",
            title: "Not Found",
            detail: "no device twin found",
            status: 404,
            instance: "",
          });
        }
        response = errorFetchAppliedEntities(
          errorDtwin.value!,
          entityIdsToFetch,
          "uid",
        );
      } else {
        response = resultDtwins.value?.results || [];
      }
      return response;
    },
  );
}

function getDeviceIds(devices: Dtwin[]): string[] {
  return devices
    .filter((device) => device.uid !== undefined)
    .map((device) => device.uid as string);
}

function getUserIds(users: User[]): string[] {
  return users
    .filter((user) => user.login !== undefined)
    .map((user) => user.login);
}

function initializeActionsAndActionTypesFilters() {
  if (LxcError.check(cacheActionAttr.value)) {
    actions.value = (
      props.filters.get(Filters.LOG_ACTION)?.value as string[]
    ).map((rawValue) => {
      return {
        rawValue,
        translation: rawValue,
      };
    });
  } else {
    actions.value = cacheActionAttr.value?.filter((action) =>
      props.filters.get(Filters.LOG_ACTION)?.value.includes(action.rawValue),
    );
  }
  if (LxcError.check(cacheActionTypesAttr.value)) {
    actionTypes.value = (
      props.filters.get(Filters.LOG_ACTION_TYPE)?.value as string[]
    ).map((rawValue) => {
      return {
        rawValue,
        translation: rawValue,
      };
    });
  } else {
    actionTypes.value = cacheActionTypesAttr.value?.filter((actionType) =>
      props.filters
        .get(Filters.LOG_ACTION_TYPE)
        ?.value.includes(actionType.rawValue),
    );
  }
}

async function initializeActionsAndActionTypesValues() {
  const [allActions, allActionTypes] = await Promise.all([
    fetchAllActions(),
    fetchAllActionTypes(),
  ]);
  cacheActionAttr.value = allActions;
  cacheActionTypesAttr.value = allActionTypes;
  initializeActionsAndActionTypesFilters();
}

const onPropsEntityChanged = async (
  paramEntityClass?: string,
  paramEntityIds?: string[],
) => {
  if (isEntityLoading.value) {
    onPropsEntityChangedPending = true;
    return;
  }

  switch (paramEntityClass) {
    case LogEntityClass.DEVICE:
      await fetchAppliedDevices(paramEntityIds);
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      devices.value = appliedDevices.value?.slice(0) ?? [];
      break;
    case LogEntityClass.USER:
      appliedUsers.value =
        paramEntityIds?.map((userId): User => {
          return {
            firstName: "",
            lastName: "",
            login: userId,
            email: "",
            active: true,
            profile: {
              id: "0",
              name: "",
            },
          };
        }) ?? [];
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      users.value = appliedUsers.value?.slice(0) ?? [];
      break;
    case LogEntityClass.GROUP:
      appliedUserGroups.value = await fetchAppliedEntities<UserGroup>(
        appliedUserGroups,
        userGroupService,
        "getUserGroupsByCode",
        "code",
        isUserGroupsLoading,
        paramEntityIds,
      );
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      break;
    case LogEntityClass.PROFILE:
      appliedProfiles.value = await fetchAppliedEntities<UserProfile>(
        appliedProfiles,
        userProfileService,
        "getUserProfilesByCodes",
        "code",
        isProfileLoading,
        paramEntityIds,
      );
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      break;
    case LogEntityClass.ROLE:
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      break;
    case LogEntityClass.SECTOR:
      appliedSectors.value = await fetchAppliedEntities<UserSessionSector>(
        appliedSectors,
        SectorsService,
        "getSectorsByCodes",
        "code",
        isSectorLoading,
        paramEntityIds,
      );
      entityClass.value = paramEntityClass;
      entityId.value = paramEntityIds;
      break;
    default:
      entityClass.value = undefined;
      entityId.value = undefined;
      devices.value = [];
      users.value = [];
      break;
  }
};

const onPropsEntityClassChanged = (entityClass?: string) => {
  onPropsEntityChanged(
    entityClass,
    props.filters.get(Filters.LOG_AUTHOR_ID)?.value as string[] | undefined,
  );
};

const onDevicesSelected = (devices: ReactiveArrayObject<Dtwin>) => {
  updateInProgress.value = true;

  if (!devices.value.length) {
    entityClass.value = undefined;
    entityId.value = undefined;
  } else {
    entityClass.value = LogEntityClass.DEVICE;
    entityId.value = getDeviceIds(devices.value);
  }

  updateInProgress.value = false;
};

const onEntityLoadingUpdated = (loading: boolean) => {
  if (!loading && onPropsEntityChangedPending) {
    onPropsEntityChangedPending = false;
    onPropsEntityChanged(
      props.filters.get(Filters.LOG_AUTHOR_CLASS)?.value as
        | LogEntityClass
        | undefined,
      props.filters.get(Filters.LOG_AUTHOR_ID)?.value as string[] | undefined,
    );
  }
};

const onUsersSelected = (users: ReactiveArrayObject<User>) => {
  updateInProgress.value = true;

  if (!users.value.length) {
    entityClass.value = undefined;
    entityId.value = undefined;
  } else {
    entityClass.value = LogEntityClass.USER;
    entityId.value = getUserIds(users.value);
  }

  updateInProgress.value = false;
};

const onChange = (filter: Filters, value: FilterSelectionValue) => {
  emit("change", filter, value);
};

const onEnter = (event: Event) => {
  emit("enter", event);
};

const logLevelOptions: Ref<(Option | Record<string, any>)[]> = ref(
  Object.values(LogLevel).map((value) => {
    return {
      value,
      label: value ? `logs.level.value.${value}` : "",
    };
  }),
);

const eventTypeOptions = computed<Option[]>((): Option[] => {
  return Object.values(LogEventType).map((value: LogEventType): Option => {
    const label = value ? t(`logs.eventType.value.${value}`) : "";
    return {
      value,
      label,
    };
  });
});

const originOptions: Ref<Option[]> = ref([
  {
    label: "logs.origin.values.device",
    value: Origin.DEVICE,
  },
  {
    label: "logs.origin.values.mobileApplication",
    value: Origin.MOBILE_APPLICATION,
  },
  {
    label: "logs.origin.values.thirdPartyApplication",
    value: Origin.THIRD_PARTY_APPLICATION,
  },
  {
    label: "logs.origin.values.lxConnect",
    value: Origin.LX_CONNECT,
  },
]);

const filterFormSections: FilterFormSection[] = [
  {
    disabled: false,
    filter: Filters.LOG_LEVEL,
    footerEnabled: true,
    footerId: "level-footer",
    header: levelLabel,
    id: "level",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: levelLabel,
    options: logLevelOptions,
    tagPrefix: levelLabel,
    translate: true,
  },
  {
    disabled: false,
    filter: Filters.LOG_ORIGIN,
    footerEnabled: true,
    footerId: "origin-footer",
    header: originLabel,
    id: "origin",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: originLabel,
    options: originOptions,
    tagPrefix: originLabel,
    translate: true,
  },
  {
    disabled: false,
    filter: Filters.LOG_EVENT_TYPE,
    footerId: "eventType-footer",
    footerEnabled: true,
    header: eventTypeLabel,
    inputType: FilterInputType.CHECKBOX,
    id: "eventType",
    menuLabel: eventTypeLabel,
    options: eventTypeOptions,
    tagPrefix: eventTypeLabel,
    translate: false,
  },
  {
    disabled: false,
    filter: Filters.LOG_ACTION,
    footerEnabled: true,
    footerId: "action-footer",
    header: actionLabel,
    id: "action",
    inputType: FilterInputType.CUSTOM,
    menuLabel: actionLabel,
    tagPrefix: actionLabel,
    translate: false,
  },
  {
    disabled: false,
    filter: Filters.LOG_ACTION_TYPE,
    footerEnabled: true,
    footerId: "action-type-footer",
    header: actionTypeLabel,
    id: "action-type",
    inputType: FilterInputType.CUSTOM,
    menuLabel: actionTypeLabel,
    tagPrefix: actionTypeLabel,
    translate: false,
  },
  {
    additionalFilter: Filters.LOG_AUTHOR_CLASS,
    disabled: false,
    filter: Filters.LOG_AUTHOR_ID,
    footerEnabled: true,
    footerId: "user-footer",
    header: userLabel,
    id: LogEntityClass.USER,
    inputType: FilterInputType.CUSTOM,
    menuLabel: userLabel,
    tagPrefix: userLabel,
  },
];

if (props.organizationCode) {
  filterFormSections.push({
    additionalFilter: Filters.LOG_AUTHOR_CLASS,
    disabled: false,
    filter: Filters.LOG_AUTHOR_ID,
    footerEnabled: true,
    footerId: "device-footer",
    header: deviceLabel,
    id: LogEntityClass.DEVICE,
    inputType: FilterInputType.CUSTOM,
    menuLabel: deviceLabel,
    tagPrefix: deviceLabel,
  });
}

function onEntityClassChanged(newEntityClass?: LogEntityClass) {
  // Disable a form section if another form section has filters
  for (const entityClass of [LogEntityClass.USER, LogEntityClass.DEVICE]) {
    let filterFormSection: FilterFormSection | undefined;

    filterFormSection = filterFormSections.find(
      (formSection) => formSection.id === entityClass,
    );

    if (filterFormSection) {
      filterFormSection.disabled =
        !!newEntityClass && entityClass !== newEntityClass;
    }
  }
}

function onAppliedFilterChange() {
  appliedActions.value = actions.value;
  appliedActionTypes.value = actionTypes.value;
  const appliedEntityClass = props.filters.get(Filters.LOG_AUTHOR_CLASS)
    ?.value as string | undefined;
  const appliedEntityId = props.filters.get(Filters.LOG_AUTHOR_ID)?.value as
    | string[]
    | undefined;

  onPropsEntityClassChanged(appliedEntityClass);
  onPropsEntityChanged(appliedEntityClass, appliedEntityId);
}

function applyFilter() {
  if (!updateInProgress.value) {
    emit(
      "change",
      Filters.LOG_ACTION,
      actions.value?.map((translated) => translated.rawValue).slice(0) ?? [],
    );
    emit(
      "change",
      Filters.LOG_ACTION_TYPE,
      actionTypes.value?.map((translated) => translated.rawValue).slice(0) ??
        [],
    );
    emit("change", Filters.LOG_AUTHOR_CLASS, entityClass.value ?? "");
    emit("change", Filters.LOG_AUTHOR_ID, entityId.value?.slice(0) ?? []);
    emit("enter");
  } else {
    applyPending.value = true;
  }
}

const onUpdateInProgressChanged = (inProgress: boolean) => {
  if (!inProgress && applyPending.value) {
    applyFilter();
    applyPending.value = false;
  }
};

function clearFilter() {
  updateInProgress.value = true;
  componentId.value = undefined;
  entityClass.value = undefined;
  entityId.value = [];
  actions.value = [];
  actionTypes.value = [];
  sortBy.value = undefined;
  sortDirection.value = undefined;
  devices.value = [];
  users.value = [];
  appliedDevices.value = [];
  updateInProgress.value = false;
  applyFilter();
}

function discardFilter() {
  updateInProgress.value = true;

  componentId.value = props.filters.get(Filters.LOG_COMPONENT_ID)?.value as
    | LogComponentId
    | undefined;
  entityClass.value = props.filters.get(Filters.LOG_AUTHOR_CLASS)?.value as
    | LogEntityClass
    | undefined;
  entityId.value = props.filters.get(Filters.LOG_AUTHOR_ID)?.value as
    | string[]
    | undefined;
  sortBy.value = props.filters.get(Filters.SORT_BY)?.value as
    | LogSortBy
    | undefined;
  sortDirection.value = props.filters.get(Filters.SORT_DIRECTION)?.value as
    | LogSortDirection
    | undefined;

  switch (entityClass.value) {
    case LogEntityClass.DEVICE:
      devices.value = appliedDevices.value?.slice(0) ?? [];
      break;
    case LogEntityClass.USER:
      users.value = appliedUsers.value?.slice(0) ?? [];
      break;
    default:
      break;
  }

  updateInProgress.value = false;
}

function onFilterShowing() {
  initializeActionsAndActionTypesFilters();
  onAppliedFilterChange();
}

function updateEntityFilterSection(
  paramFilterMap: FiltersSelection,
  entityClass: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramUserGroups?: UserGroup[],
  paramProfiles?: UserProfile[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
) {
  let entityIds: string[] = [];

  switch (entityClass) {
    case LogEntityClass.DEVICE:
      if (entityClass === LogEntityClass.DEVICE) {
        if (paramDevices) {
          entityIds = filterEmptyValues<string>(
            paramDevices.map((device) => device.uid),
          );
        }
      }
      break;
    case LogEntityClass.GROUP:
      if (paramUserGroups) {
        entityIds = filterEmptyValues<string>(
          paramUserGroups.map((userGroup) => userGroup.code),
        );
      }
      break;
    case LogEntityClass.PROFILE:
      if (paramProfiles) {
        entityIds = filterEmptyValues<string>(
          paramProfiles.map((profile) => profile.code),
        );
      }
      break;
    case LogEntityClass.ROLE:
      if (paramRoles) {
        entityIds = filterEmptyValues<string>(
          paramRoles.map((role) => (role.id ? String(role.id) : undefined)),
        );
      }
      break;
    case LogEntityClass.SECTOR:
      if (paramSectors) {
        entityIds = filterEmptyValues<string>(
          paramSectors.map((sector) => sector.code),
        );
      }
      break;
    case LogEntityClass.USER:
      if (paramUsers) {
        entityIds = filterEmptyValues<string>(
          paramUsers.map((user) => user.login),
        );
      }
      break;
    default:
      break;
  }

  paramFilterMap.set(Filters.LOG_AUTHOR_CLASS, {
    key: "authorClass",
    operator: "=",
    value: entityClass,
  });

  paramFilterMap.set(Filters.LOG_AUTHOR_ID, {
    key: "authorId",
    operator: "=",
    value: entityIds,
  });
}

function updateFilterSelection(
  filters: FiltersSelection,
  actions?: TranslatedLogAttribute[],
  actionTypes?: TranslatedLogAttribute[],
  entityClass?: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramGroups?: UserGroup[],
  paramProfiles?: UserProfile[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
): FiltersSelection {
  filters.set(Filters.LOG_ACTION, {
    key: "action",
    operator: "=",
    value:
      actions?.map(
        (translatedLogAttribute) => translatedLogAttribute.rawValue,
      ) ?? [],
  });
  filters.set(Filters.LOG_ACTION_TYPE, {
    key: "actionType",
    operator: "=",
    value:
      actionTypes?.map(
        (translatedLogAttribute) => translatedLogAttribute.rawValue,
      ) ?? [],
  });

  if (entityClass) {
    updateEntityFilterSection(
      filters,
      entityClass,
      paramDevices,
      paramUsers,
      paramGroups,
      paramProfiles,
      paramRoles,
      paramSectors,
    );
  }

  return filters;
}

function deleteDeviceFilter(id: string) {
  const index = devices.value.findIndex((device) => device.uid === id);

  if (index >= 0) {
    devices.value.splice(index, 1);
  }
}

function deleteUserFilter(id: string) {
  const index = users.value.findIndex((user) => user.login === id);

  if (index >= 0) {
    users.value.splice(index, 1);
  }
}

function deleteEntityTag(id: string) {
  switch (entityClass.value) {
    case LogEntityClass.DEVICE:
      deleteDeviceFilter(id);
      break;
    case LogEntityClass.USER:
      deleteUserFilter(id);
      break;
    default:
      break;
  }
}

function deleteTagFromList(valueToDelete: string, list?: string[] | null) {
  if (list) {
    const index = list.findIndex((value) => value === valueToDelete);

    if (index != null && index >= 0) {
      list.splice(index, 1);
    }
  }
}

function buildEntityTagFilter(
  entityClass?: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramGroups?: UserGroup[],
  paramProfiles?: UserProfile[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
): UniqueLabel[] {
  let tagFilters: UniqueLabel[] = [];
  switch (entityClass) {
    case LogEntityClass.DEVICE:
      if (paramDevices) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramDevices.map((device) =>
            filtersUtils.getDtwinTag(device, deviceLabel),
          ),
        );
      }
      break;
    case LogEntityClass.GROUP:
      if (paramGroups) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramGroups.map((userGroup) =>
            filtersUtils.getUserGroupTag(userGroup, userGroupLabel),
          ),
        );
      }
      break;
    case LogEntityClass.PROFILE:
      if (paramProfiles) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramProfiles.map((profile) =>
            filtersUtils.getProfileTag(profile, profileLabel),
          ),
        );
      }
      break;
    case LogEntityClass.ROLE:
      if (paramRoles) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramRoles.map((role) => filtersUtils.getRoleTag(role, roleLabel)),
        );
      }
      break;
    case LogEntityClass.SECTOR:
      if (paramSectors) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramSectors.map((sector) =>
            filtersUtils.getSectorTag(sector, sectorLabel),
          ),
        );
      }
      break;
    case LogEntityClass.USER:
      if (paramUsers) {
        tagFilters = filterEmptyValues<UniqueLabel>(
          paramUsers.map((user) => filtersUtils.getUserTag(user, userLabel)),
        );
      }
      break;
    default:
  }

  return tagFilters;
}

function buildFilterTags(
  actions?: TranslatedLogAttribute[],
  actionTypes?: TranslatedLogAttribute[],
  entityClass?: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramGroups?: UserGroup[],
  paramProfiles?: UserProfile[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
): UniqueLabel[] {
  const tagFilters: UniqueLabel[] = [];

  if (actions && actions.length !== 0) {
    for (const action of actions) {
      const filterTag = filtersUtils.getTag(
        actionLabel,
        action.rawValue,
        action.translation,
      );
      if (filterTag) {
        tagFilters.push(filterTag);
      }
    }
  }
  if (actionTypes && actionTypes.length !== 0) {
    for (const actionType of actionTypes) {
      const filterTag = filtersUtils.getTag(
        actionTypeLabel,
        actionType.rawValue,
        actionType.translation,
      );
      if (filterTag) {
        tagFilters.push(filterTag);
      }
    }
  }

  if (entityClass) {
    tagFilters.push(
      ...buildEntityTagFilter(
        entityClass,
        paramDevices,
        paramUsers,
        paramGroups,
        paramProfiles,
        paramRoles,
        paramSectors,
      ),
    );
  }

  return tagFilters;
}

const selectedFilterTags = computed((): UniqueLabel[] => {
  return buildFilterTags(
    actions.value,
    actionTypes.value,
    entityClass.value,
    devices.value,
    users.value,
  );
});

const selectedFilterMap = computed((): FiltersSelection => {
  const filterSelection: FiltersSelection = new Map<
    Filters,
    FilterSelectionDefinition
  >();
  return updateFilterSelection(
    filterSelection,
    actions.value,
    actionTypes.value,
    entityClass.value,
    devices.value,
    users.value,
  );
});

const appliedFilterTags = computed((): UniqueLabel[] => {
  const appliedEntityClass = props.filters.get(Filters.LOG_AUTHOR_CLASS)
    ?.value as LogEntityClass | undefined;
  return buildFilterTags(
    appliedActions.value,
    appliedActionTypes.value,
    appliedEntityClass,
    appliedDevices.value,
    appliedUsers.value,
    appliedUserGroups.value,
    appliedProfiles.value,
    appliedRoles.value,
    appliedSectors.value,
  );
});

function onDeleteSelectedTag(tag: string, uid?: string) {
  updateInProgress.value = true;
  actions.value = actions.value?.filter(
    (action) => action.rawValue !== (uid ?? tag),
  );
  actionTypes.value = actionTypes.value?.filter(
    (actionTypes) => actionTypes.rawValue !== (uid ?? tag),
  );
  deleteEntityTag(uid ?? tag);
  updateInProgress.value = false;
}

function onAppliedTagDeleteClick(tag: string, uid?: string) {
  updateInProgress.value = true;
  actions.value = actions.value?.filter(
    (action) => action.rawValue !== (uid ?? tag),
  );
  actionTypes.value = actionTypes.value?.filter(
    (actionTypes) => actionTypes.rawValue !== (uid ?? tag),
  );
  deleteTagFromList(uid ?? tag, entityId.value);

  if (!entityId.value?.length && entityClass.value !== undefined) {
    entityClass.value = undefined;
  }

  updateInProgress.value = false;
  applyFilter();
}

watch(() => devices, onDevicesSelected, { deep: true });
watch(() => entityClass.value, onEntityClassChanged);
watch(() => isEntityLoading.value, onEntityLoadingUpdated);
watch(() => users, onUsersSelected, { deep: true });
watch(() => updateInProgress.value, onUpdateInProgressChanged);

defineExpose({
  clearFilter,
});

onMounted(async () => {
  await initializeActionsAndActionTypesValues();
  onAppliedFilterChange();
  isMounted.value = true;
});
</script>

<template>
  <div class="relative flex justify-between">
    <log-download :data="props.rowsSelected" />
    <div class="relative flex justify-end">
      <lxc-period-picker
        v-model="formPeriod"
        type="primary"
        :can-clear-selection="canClearPeriodSelection"
        button-size
        :formatter="t('logs.filters.timestamp.formatter')"
        :i18n="locale"
        :separator="periodSeparator"
        :placeholder="t('logs.filters.timestamp.placeholder')"
        class="mr-3"
      />

      <filters-component
        v-if="isMounted"
        :applied-tags="appliedFilterTags"
        :filter-sections="filterFormSections"
        :filters="props.filters"
        :filters-by-type="selectedFiltersByTypes"
        :selected-filters="selectedFilterMap"
        :selected-tags="selectedFilterTags"
        :teleported-tags="tagsetSelector"
        @apply="applyFilter"
        @applied-filter-change="onAppliedFilterChange"
        @change="onChange"
        @delete="onAppliedTagDeleteClick"
        @delete-selected="onDeleteSelectedTag"
        @enter="onEnter"
        @discard="discardFilter"
        @reset="clearFilter"
        @showing="onFilterShowing"
      >
        <template #action>
          <log-action-action-type-filter
            v-model="actions"
            :context="ActionsOrActionTypesContext.ACTION"
            :label="actionLabel"
          />
        </template>

        <template #action-type>
          <log-action-action-type-filter
            v-model="actionTypes"
            :context="ActionsOrActionTypesContext.ACTION_TYPES"
            :label="actionTypeLabel"
          />
        </template>

        <template #device>
          <log-device-filter
            v-model="devices.value"
            :label="deviceLabel"
            :organization-code="organizationCode"
          />
        </template>

        <template #user>
          <log-user-filter
            v-model="users.value"
            :label="userLabel"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
          />
        </template>
      </filters-component>
    </div>
  </div>
  <div :id="tagsetId" />
</template>
